
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Book, ShieldCheck, HelpCircle, FileText, GraduationCap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const Terms = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="prose max-w-none">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold m-0">Terms & Acknowledgments</h1>
          </div>
<p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Book className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">Overview</h2>
            </div>
            <p className="text-gray-700">
              Bright Stages, LLC ("Bright Stages," "we," "our," or "us") is dedicated to providing research-based developmental assessments for children ages 0-6. Our test questions and milestone tracking methodologies are designed in alignment with established child development research, early learning standards, and best practices in pediatric screening. While our assessments are informed by widely accepted guidelines, all proprietary wording, test formats, and content are the exclusive intellectual property of Bright Stages, LLC.
            </p>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold m-0">Terms of Service</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">1. Use of Services</h3>
                <p className="text-gray-700">
                  Bright Stages, LLC ("Bright Stages," "we," "our," or "us") provides research-backed developmental assessments and milestone tracking tools for children ages 0-6. These services are intended solely for educational and informational purposes and should not be used as a substitute for professional medical, developmental, or psychological evaluations.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">2. User Responsibilities</h3>
                <p className="text-gray-700">By using Bright Stages, users agree to:</p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Provide accurate and truthful information when utilizing the platform.</li>
                  <li>Use the services only for their intended purpose.</li>
                  <li>Refrain from any misuse, including but not limited to duplication, modification, reproduction, or unauthorized distribution of Bright Stages' content. Any such actions are strictly prohibited and may result in legal consequences.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">3. User Content & Permissions</h3>
                <p className="text-gray-700">
                  Users who submit, upload, or share content on the Bright Stages platform grant Bright Stages, LLC a limited, non-exclusive, worldwide, royalty-free license to use, display, and analyze such content for research, educational, and analytical purposes.
                </p>
                <p className="text-gray-700 mt-4">
                  By submitting content, users affirm that they own or have the necessary rights to provide the material and that their submission does not infringe upon third-party rights. Bright Stages reserves the right to remove any content that violates these terms or applicable laws.
                </p>
              </div>

<div>
  <h3 className="text-xl font-semibold mb-3">
    4. Subscription and Assessment Purchase Terms
  </h3>

  {/* A. Subscription Plans */}
  <p className="text-gray-700 font-semibold mt-2">
    A. Subscription Plans
  </p>
  <p className="text-gray-700 mt-2">
    Bright Stages offers the following subscription plans:
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>Monthly Subscription: $9.99 per month</li>
    <li>Yearly Subscription: $59.88 per year</li>
    <li>Lifetime Subscription: $199.99 (one-time payment)</li>
  </ul>

  <p className="text-gray-700 mt-4 font-medium">
    Billing &amp; Changes:
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>
      Users may subscribe to a monthly or yearly plan and can upgrade,
      downgrade, or cancel their subscription at any time.
    </li>
    <li>
      Subscription fees are charged automatically at the start of each billing
      cycle (monthly or yearly) unless canceled prior to the renewal date.
    </li>
    <li>
      Switching between monthly and yearly subscriptions is prorated. If a user
      moves from a yearly to a monthly plan, the new billing cycle starts after
      the current term ends. Lifetime plans are a one-time purchase and are not
      eligible for proration.
    </li>
  </ul>

  {/* B. Child Development Screening Assessments */}
  <p className="text-gray-700 font-semibold mt-6">
    B. Child Development Screening Assessments ($20 Assessments)
  </p>
  <p className="text-gray-700 mt-2">
    In addition to subscription plans, Bright Stages offers stand-alone Child
    Development Screening Assessments (&quot;Assessments&quot;) that may be
    purchased without a subscription.
  </p>

  <p className="text-gray-700 mt-4 font-medium">
    Assessment Types and Age Ranges
  </p>
  <p className="text-gray-700 mt-2">
    Bright Stages currently offers Assessments for:
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>Infant (0–11 months)</li>
    <li>Age-based screens through Age 6</li>
    <li>Kindergarten Readiness Assessment for children ages 4–6</li>
  </ul>
  <p className="text-gray-700 mt-2">
    The specific titles and age ranges of Assessments may be updated from time
    to time as Bright Stages improves and expands its offerings.
  </p>

  <p className="text-gray-700 mt-4 font-medium">
    Pricing and Scope of Use
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>Each Assessment is priced at $20 per screening.</li>
    <li>
      The purchase of an Assessment provides access to one specific screening
      test for use with one child in a single household.
    </li>
    <li>
      Assessment access and results are for personal, non-commercial use only
      and may not be shared, resold, or used to provide services to third
      parties.
    </li>
  </ul>

  <p className="text-gray-700 mt-4 font-medium">
    Access and Test Link Usage
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>
      After purchase, users receive a confirmation email containing a unique
      test link for the selected Assessment.
    </li>
    <li>
      Each unique test link allows up to two completed submissions of that
      Assessment. This is intended to permit an initial screening and one
      follow-up reassessment to check the child&apos;s progress.
    </li>
    <li>
      Bright Stages may specify an access or expiration period for each
      Assessment link, which will be stated at the time of purchase or in the
      confirmation email. After the expiration date, unused links or remaining
      attempts may no longer be available.
    </li>
  </ul>

  <p className="text-gray-700 mt-4 font-medium">
    Relationship to Subscription Plans
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>
      Purchasing a $20 Assessment does not create or renew a subscription and
      does not change the status of any existing subscription.
    </li>
    <li>
      Canceling a subscription does not automatically cancel or remove access to
      any Assessment already purchased, subject to any access or expiration
      limits described at the time of purchase.
    </li>
  </ul>

  <p className="text-gray-700 mt-4 font-medium">
    Refunds for Assessments
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>
      Assessment purchases are generally non-refundable and are subject to the
      refund practices described in Section 10 (Refunds).
    </li>
    <li>
      Refunds or credits may be considered only in limited circumstances such as
      billing errors or duplicate charges.
    </li>
    <li>
      Failure to use a purchased Assessment, dissatisfaction with results, or
      expiration of a test link do not qualify for a refund.
    </li>
  </ul>

  <p className="text-gray-700 mt-4 font-medium">
    Informational Nature of Assessments
  </p>
  <ul className="list-disc pl-6 text-gray-700 mt-2">
    <li>
      All Assessments are intended solely for educational and developmental
      screening purposes and do not constitute medical, psychological, or
      diagnostic evaluations.
    </li>
    <li>
      Assessment results do not guarantee admission, placement, or readiness
      determinations by any school, program, or institution. Users are
      encouraged to consult qualified professionals if they have concerns about
      their child&apos;s development.
    </li>
  </ul>
</div>

              <div>
                <h3 className="text-xl font-semibold mb-3">5. Privacy & Data Security</h3>
                <p className="text-gray-700">
                  Bright Stages, LLC does not collect personally identifiable information about children. Any data shared through the platform is encrypted and secured in accordance with industry standards to ensure user privacy and data protection.
                </p>
                <p className="text-gray-700 mt-4">
                By creating an account on the Bright Stages platform and/or purchasing any subscription or Child Development Screening Assessment, you consent to receive emails from Bright Stages, including transactional messages (such as receipts, account updates, and assessment access links) and our email newsletter with product updates, educational content, and special offers.
You may opt out of non-transactional marketing emails at any time by clicking the “unsubscribe” link in any newsletter or by contacting us at info@BrightStages.com. Even if you opt out of marketing emails, we may still send you transactional or service-related messages that are necessary to provide and support your use of the platform and assessments.
              </p>
                </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">6. Limitations of Liability</h3>
                <p className="text-gray-700">
                  Bright Stages, LLC makes no warranties regarding the accuracy, completeness, or applicability of the developmental assessments provided. Users assume full responsibility for how they interpret and apply the results. Bright Stages, LLC shall not be liable for any errors, inaccuracies, or misuse of the assessments, nor for any direct, indirect, incidental, or consequential damages arising from the use of our services.
                </p>
                <p className="text-gray-700 mt-4">
                  Bright Stages, LLC and its affiliates, officers, employees, or licensors shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from or related to the use of our platform, including but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mt-2">
                  <li>Data loss or corruption</li>
                  <li>Technical errors, service interruptions, or security breaches</li>
                  <li>User reliance on assessment results or milestone tracking</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">7. Intellectual Property</h3>
                <p className="text-gray-700">
                  All content, test formats, milestone evaluations, and related materials available on Bright Stages' platform are the exclusive intellectual property of Bright Stages, LLC and are protected under applicable copyright and intellectual property laws. Users may not copy, modify, distribute, or create derivative works from any portion of the platform's content without express written permission from Bright Stages, LLC.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">8. Modifications to Services</h3>
                <p className="text-gray-700">
                  Bright Stages, LLC reserves the right to modify, suspend, or discontinue any aspect of its services, including assessments, tools, or platform features, at any time and without prior notice. Bright Stages, LLC shall not be liable for any modification, suspension, or discontinuation of services.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">9. Governing Law</h3>
                <p className="text-gray-700">
                  These Terms of Service shall be governed by and construed in accordance with the laws of the State of Nevada, without regard to its conflict of law principles. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts located in Nevada.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">10. Refunds</h3>
                <p className="text-gray-700">
                  All subscription purchases made through Bright Stages, LLC are non-refundable. By subscribing to our services, users acknowledge and agree that all payments are final and that no refunds, partial or full, will be issued for any reason, including but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mt-2">
                  <li>User dissatisfaction with the service.</li>
                  <li>Failure to use the purchased assessments.</li>
                  <li>Accidental or unintended purchases.</li>
                  <li>Service modifications, updates, or discontinuation.</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  Users are encouraged to review their subscription details carefully before completing a purchase. If any billing issues arise, users may contact Bright Stages, LLC for assistance.
                </p>
                <p className="text-gray-700 mt-4">
                  Refunds may be considered in cases of:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mt-2">
                  <li>Billing errors</li>
                  <li>Duplicate charges</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <h2 className="text-2xl font-semibold mb-4">Copyright & Compliance Statement</h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Bright Stages, LLC ("Bright Stages") develops independently designed developmental assessments that do not reproduce, replicate, or contain verbatim content from proprietary tests, including but not limited to Brigance® or ASQ®.
              </p>
              <p className="text-gray-700">
                Our test formats, milestone evaluations, and parent-guided activities are uniquely structured to provide engaging and developmentally appropriate assessments. All assessment content is original to Bright Stages and is protected under applicable copyright and intellectual property laws.
              </p>
              <p className="text-gray-700">
                Bright Stages complies with public domain usage laws and fair use policies by referencing established scientific research while ensuring that all assessment materials remain original and do not infringe upon third-party copyrights.
              </p>
              <p className="text-gray-700">
                The information provided through Bright Stages is intended solely for educational and developmental screening purposes and should not be used as a substitute for professional medical, psychological, or developmental evaluations.
              </p>
            </div>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <h2 className="text-2xl font-semibold mb-4">Sources & Research-Based References</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">1. Developmental Milestones & Screening Standards</h3>
                <ul className="space-y-4 text-gray-700">
                  <li>
                    <p className="font-medium">Centers for Disease Control and Prevention (CDC). (2021). Learn the Signs. Act Early: Developmental Milestones Checklist.</p>
                    <p className="ml-4 mt-1">Public domain resource for child development tracking.</p>
                    <p className="ml-4">CDC Milestones</p>
                  </li>
                  <li>
                    <p className="font-medium">American Academy of Pediatrics (AAP). (2016). Bright Futures: Guidelines for Health Supervision of Infants, Children, and Adolescents (4th Ed.).</p>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">2. Early Learning Standards & School Readiness Guidelines</h3>
                <ul className="space-y-4 text-gray-700">
                  <li>
                    <p className="font-medium">National Association for the Education of Young Children (NAEYC).</p>
                    <p className="ml-4 mt-1">Developmentally Appropriate Practice Guidelines</p>
                  </li>
                  <li>
                    <p className="font-medium">State & International Learning Frameworks (Examples):</p>
                    <ul className="ml-4 mt-1 space-y-2">
                      <li>California Preschool Learning Foundations (2019)</li>
                      <li>Ontario Early Years Curriculum (2014)</li>
                      <li>EYFS Framework (Early Years Foundation Stage, UK Government, 2021)</li>
                    </ul>
                  </li>
                  <li>
                    <p className="font-medium">Montessori & Play-Based Learning Research:</p>
                    <ul className="ml-4 mt-1 space-y-2">
                      <li>Montessori, M. (1912). The Montessori Method.</li>
                      <li>Ginsburg, K. R. (2007). The Importance of Play in Promoting Healthy Child Development and Maintaining Strong Parent-Child Bonds. Pediatrics, 119(1), 182-191.</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">3. Child Cognitive & Social-Emotional Development Research</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>Piaget, J. (1952). The Origins of Intelligence in Children.</li>
                  <li>Vygotsky, L. S. (1978). Mind in Society: The Development of Higher Psychological Processes.</li>
                  <li>Goleman, D. (1995). Emotional Intelligence: Why It Can Matter More Than IQ.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <h2 className="text-2xl font-semibold mb-4">Acknowledgments</h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                We acknowledge the educators, pediatricians, and child development specialists whose research has shaped the foundation of early childhood assessment. By combining scientific research with engaging, parent-led activities, Bright Stages, LLC aims to empower families with meaningful insights into their child's growth and learning journey.
              </p>
              <p className="text-gray-700">
                For further inquiries about our research-based approach, please contact us at: info@BrightStages.com.
              </p>
            </div>
          </section>

          {/* Footer Links */}
          <footer className="py-8 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 text-sm md:text-base md:space-x-8">
                <div className="flex space-x-4 md:space-x-8">
                  <Button 
                    variant="link" 
                    onClick={() => navigate("/privacy")} 
                    className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
                  >
                    Privacy Policy
                  </Button>
                  <Button 
                    variant="link" 
                    onClick={() => navigate("/legal-information")} 
                    className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
                  >
                    Legal Information
                  </Button>
                </div>
                <Button 
                  variant="link" 
                  onClick={() => navigate("/terms")} 
                  className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
                >
                  Terms & Acknowledgments
                </Button>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default Terms;
